import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CircleNotch, Warning, UserMinus, ArrowsLeftRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { TransferOwnershipWizard } from './TransferOwnershipWizard';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Deactivate an account — access off, account preserved. Account deletion does not exist
 * (CLAUDE.md #25). What the user owns is shown for information only: deactivation never transfers,
 * deletes or changes it (Phase 4-D rule L2). Transferring ownership is a separate, optional action.
 */

interface OwnershipCategory { key: string; label: string; count: number; reassignable: boolean }
interface OwnershipReport {
  ownership: OwnershipCategory[];
  liveOwnershipCount: number;
}

const input =
  'h-9 w-full rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-1 focus:ring-plat-focus/50';

export function DeactivateUserDialog({
  open, onClose, user, onDeactivated,
}: {
  open: boolean;
  onClose: () => void;
  user: { id: string; name: string; chambersId: string };
  onDeactivated?: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);

  const ownershipQ = useQuery({
    queryKey: ['platform', 'users', user.id, 'dependencies'],
    queryFn: async () => {
      const res = await platformApi.get(`/users/${user.id}/dependencies`);
      return (res.data?.data ?? res.data) as OwnershipReport;
    },
    enabled: open,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => platformApi.post(`/users/${user.id}/deactivate`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success('Account deactivated — signed out of every session');
      queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
      setReason('');
      onDeactivated?.();
      onClose();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Could not deactivate the account')),
  });

  // Escape closes the dialog (not while the request is in flight, and not over the transfer wizard).
  const busy = deactivateMutation.isPending;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy && !transferOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, transferOpen, onClose]);

  if (!open) return null;

  const owned = (ownershipQ.data?.ownership ?? []).filter((o) => o.count > 0);
  const canConfirm = reason.trim().length >= 3;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 p-4 sm:items-center"
        onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      >
        <ScrollLock />
        <div role="dialog" aria-modal="true" aria-label="Deactivate account" className="w-full max-w-md rounded-2xl border border-plat-border bg-plat-elevated shadow-2xl">
          <div className="flex items-center justify-between border-b border-plat-border px-5 py-4">
            <h3 className="flex items-center gap-2 text-base font-semibold text-plat-fg">
              <UserMinus className="h-4 w-4 text-plat-warning-text" /> Deactivate account
            </h3>
            <button onClick={onClose} aria-label="Close" className="rounded p-1 text-plat-faint hover:bg-plat-hover hover:text-plat-secondary">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 px-5 py-5">
            <div className="flex items-start gap-2.5 rounded-lg border border-plat-warning-line bg-plat-warning-soft px-3 py-3">
              <Warning className="mt-0.5 h-4 w-4 shrink-0 text-plat-warning-text" weight="fill" />
              <p className="text-sm text-plat-warning-text">
                {user.name} will be signed out of every session and will not be able to sign in. The account
                and everything linked to it — matters, documents, communications, financial records and
                history — are kept exactly as they are. You can reactivate the account at any time.
              </p>
            </div>

            {ownershipQ.isLoading ? (
              <div className="py-3 text-center"><CircleNotch className="mx-auto h-5 w-5 animate-spin text-plat-info-text" /></div>
            ) : owned.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-plat-faint">Currently owns</p>
                <div className="space-y-1.5">
                  {owned.map((o) => (
                    <div key={o.key} className="flex items-center justify-between rounded-lg border border-plat-border bg-plat-surface px-3 py-2 text-sm">
                      <span className="text-plat-secondary">{o.label}</span>
                      <span className="font-semibold text-plat-fg">{o.count}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-plat-faint">
                  Deactivation does not change ownership. If this work should move to someone else, transfer it
                  separately.
                </p>
                {(ownershipQ.data?.liveOwnershipCount ?? 0) > 0 && (
                  <button
                    onClick={() => setTransferOpen(true)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-plat-border bg-plat-surface px-3 py-1.5 text-xs font-medium text-plat-secondary hover:bg-plat-hover"
                  >
                    <ArrowsLeftRight className="h-3.5 w-3.5" /> Transfer ownership (optional)
                  </button>
                )}
              </div>
            )}

            <div>
              <label htmlFor="deactivate-reason" className="mb-1.5 block text-xs font-medium text-plat-muted">Reason (required)</label>
              <input id="deactivate-reason" className={input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Left chambers" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-plat-border px-5 py-4">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-muted hover:bg-plat-hover">
              Cancel
            </button>
            <button
              disabled={!canConfirm || deactivateMutation.isPending}
              onClick={() => deactivateMutation.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-plat-warning-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-warning-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
            >
              {deactivateMutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />}
              Deactivate account
            </button>
          </div>
        </div>
      </div>

      <TransferOwnershipWizard
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        user={{ id: user.id, chambersId: user.chambersId, name: user.name }}
        onDone={() => { setTransferOpen(false); ownershipQ.refetch(); }}
      />
    </>
  );
}
